import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import logo from '@/assets/logo-new.png'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Menu, ShoppingBag, User } from 'lucide-react'
import CartDrawer from './CartDrawer'
import { useActiveSection } from '@/hooks/use-active-section'
import { cn } from '@/lib/utils'

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const activeSection = useActiveSection()

  const isActive = (href: string) => {
    if (href === '/' && location.pathname === '/') return true
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '')
      return location.pathname === '/' && activeSection === sectionId
    }
    return location.pathname === href
  }

  const menuItems = [
    { label: 'HOME', href: '/' },
    { label: 'PRODUTOS', href: '/#produtos' },
    { label: 'PROPÓSITO', href: '/proposito' },
    { label: 'O QUE FAZEMOS', href: '/diferenciais' },
    { label: 'BLOG', href: '/blog' },
    { label: 'FAQ', href: '/faq' },
    { label: 'CONTATO', href: '/contato' },
  ]

  const handleMenuClick = () => {
    setMobileMenuOpen(false)
  }

  return (
    <header className="bg-background sticky top-0 z-50 shadow-sm">
      {/* Desktop Header - Two rows with logo spanning both */}
      <div className="hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex">
            {/* Logo - Left side, spanning both rows */}
            <a href="/" className="flex items-center py-2 pr-6">
              <img
                src={logo}
                alt="FotoLivro Store"
                className="h-20 w-auto"
                width={120}
                height={80}
              />
            </a>

            {/* Right side - Two rows */}
            <div className="flex-1 flex flex-col pl-6">
              {/* Top Row - Utility buttons */}
              <div className="flex items-center justify-end h-12">
                <div className="flex items-center gap-5">
                  <a
                    href={`${import.meta.env.VITE_URL_LOGIN}`}
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-foreground hover:text-primary transition-colors font-medium"
                  >
                    <User className="h-3.5 w-3.5" />
                    ENTRAR
                  </a>
                </div>
              </div>

              {/* Bottom Row - Navigation */}
              <nav className="flex items-center justify-center h-12 gap-8 font-gravesend font-medium">
                {menuItems.map(item => (
                  <a
                    key={item.label}
                    href={item.href}
                    className={cn(
                      'text-sm tracking-widest transition-all duration-300 relative font-semibold',
                      "after:content-[''] after:absolute after:w-full after:h-px after:bottom-[-4px] after:left-0",
                      'after:bg-primary after:origin-center after:transition-transform after:duration-300',
                      'hover:after:scale-x-100',
                      isActive(item.href)
                        ? 'text-primary after:scale-x-100'
                        : 'text-foreground hover:text-primary after:scale-x-0',
                    )}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="w-px h-5 bg-border mx-2" />
                <a
                  href="/parceria"
                  className={cn(
                    'text-sm tracking-widest font-medium transition-all duration-300',
                    isActive('/parceria')
                      ? 'text-primary font-semibold'
                      : 'text-muted-foreground hover:text-primary',
                  )}
                >
                  QUERO SER PARCEIRO
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <a href="/">
              <img
                src={logo}
                alt="FotoLivro Store"
                className="h-12 w-auto"
                width={72}
                height={48}
              />
            </a>

            <div className="flex items-center gap-2">
              <CartDrawer />
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Abrir menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                  <nav className="flex flex-col gap-4 mt-8 font-gravesend">
                    {menuItems.map(item => (
                      <a
                        key={item.label}
                        href={item.href}
                        onClick={handleMenuClick}
                        className={cn(
                          'text-lg tracking-wider py-3 px-4 -mx-4 rounded-lg border-b border-border',
                          'transition-all duration-300 ease-out',
                          'hover:bg-primary/5 hover:border-primary/20',
                          isActive(item.href)
                            ? 'text-primary border-primary bg-primary/10 font-semibold'
                            : 'text-foreground hover:text-primary font-medium',
                        )}
                      >
                        {item.label}
                      </a>
                    ))}
                    <a
                      href="/parceria"
                      onClick={handleMenuClick}
                      className="text-lg tracking-wider font-medium text-muted-foreground py-3 px-4 -mx-4 rounded-lg border-b border-border transition-all duration-300 ease-out hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                    >
                      QUERO SER PARCEIRO
                    </a>
                    <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-border">
                      <a
                        href={`${import.meta.env.VITE_URL_LOGIN}`}
                        onClick={handleMenuClick}
                        className="flex items-center justify-center gap-2 text-base font-medium text-foreground border border-border px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                      >
                        <User className="h-5 w-5" />
                        Entrar
                      </a>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
